import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Award, Users, Heart, BadgeCheck } from "lucide-react";

const TEAM = [
  { name: "Alpha Leadership", role: "Executive & Governance", img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "Operations Team", role: "Service Delivery & Rostering", img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "Clinical Team", role: "Community Nursing & Clinical Oversight", img: "https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "Housing Team", role: "SIL & SDA Portfolio", img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
];

export default function AboutPage() {
  return (
    <div data-testid="page-about">
      <section className="alpha-gradient-hero border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container pt-14 pb-12">
          <div className="alpha-overline">About Alpha</div>
          <h1 className="mt-3 text-4xl sm:text-[56px] font-display font-medium tracking-tight leading-[1.05]">A registered NDIS provider, built around the person.</h1>
          <p className="mt-5 max-w-2xl text-lg text-[color:var(--alpha-mute)]">
            Alpha Community Care delivers personalised NDIS supports across Australia — nationwide, with hubs in Melbourne & Perth. We operate with a single focus: helping participants live the life they choose — with independence, dignity, choice and collaboration at the heart of every decision.
          </p>
        </div>
      </section>

      <section className="alpha-section">
        <div className="alpha-container grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { Icon: ShieldCheck, k: "NDIS Registered", v: "Operating across Victoria & WA" },
            { Icon: Award, k: "Practice Standards", v: "NDIS Practice Standards compliant" },
            { Icon: BadgeCheck, k: "Safeguarding", v: "Documented quality framework" },
            { Icon: Users, k: "Person-centred", v: "Supports shaped by participants" },
          ].map((c) => (
            <div key={c.k} className="alpha-card p-6">
              <c.Icon className="w-6 h-6 text-[color:var(--alpha-purple-700)]" />
              <div className="mt-3 font-display text-lg font-semibold">{c.k}</div>
              <div className="text-sm text-[color:var(--alpha-mute)]">{c.v}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="compliance" className="alpha-section bg-[color:var(--alpha-bg-soft)]">
        <div className="alpha-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="alpha-overline">Quality & compliance</div>
            <h2 className="mt-3 text-3xl sm:text-[40px] font-display font-medium">Trust written into how we operate.</h2>
            <p className="mt-4 text-[color:var(--alpha-mute)]">Our quality framework is embedded in daily practice — from recruitment and training to service planning and review. Participants, families and coordinators can see how we run.</p>
            <Link to="/policies" className="alpha-btn-ghost mt-6 text-sm">Read our policies</Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              ["NDIS Practice Standards", "Audited against governance, service delivery, workforce and safeguarding."],
              ["Qualified & verified staff", "Worker screening, qualifications, supervision and continuous training."],
              ["Clinical oversight", "Registered nurses, positive behaviour support and allied health collaboration."],
              ["Incident management", "Documented incident, risk and escalation processes — reviewed monthly."],
            ].map(([t, d]) => (
              <div key={t} className="alpha-card p-6">
                <h3 className="font-display text-lg font-semibold">{t}</h3>
                <p className="text-sm text-[color:var(--alpha-mute)] mt-1">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="alpha-section">
        <div className="alpha-container">
          <div className="alpha-overline">Our teams</div>
          <h2 className="mt-3 text-3xl sm:text-[40px] font-display font-medium">Experienced operators, everyday practitioners.</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TEAM.map((m) => (
              <div key={m.name} className="alpha-card overflow-hidden">
                <div className="h-56 overflow-hidden">
                  <img src={m.img} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <div className="font-display text-lg font-semibold">{m.name}</div>
                  <div className="text-sm text-[color:var(--alpha-mute)]">{m.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="alpha-section bg-[color:var(--alpha-bg-soft)]">
        <div className="alpha-container grid md:grid-cols-2 gap-10">
          <div>
            <div className="alpha-overline">Our values</div>
            <h3 className="mt-3 font-display text-3xl font-medium">Independence. Dignity. Choice. Collaboration.</h3>
            <p className="mt-4 text-[color:var(--alpha-mute)]">We exist to deliver better days for people living with disability — with homes that feel like homes, teams that genuinely listen, and supports you can measure.</p>
            <Link to="/our-values" className="alpha-btn-ghost mt-6 text-sm">Read about our values</Link>
          </div>
          <ul className="space-y-4">
            {[
              ["Person-centred", "Every plan is shaped around the participant, not the provider."],
              ["Plain-English", "Clear written communication, transparent decisions, honest conversations."],
              ["Measured outcomes", "We track goals and progress — and adjust when something isn't working."],
            ].map(([t, d]) => (
              <li key={t} className="alpha-card p-5 flex gap-4">
                <Heart className="w-5 h-5 text-[color:var(--alpha-green-600)] shrink-0" />
                <div>
                  <div className="font-display font-semibold">{t}</div>
                  <div className="text-sm text-[color:var(--alpha-mute)]">{d}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
